/**
 * Dynamic service loaders for assistant silent create (request 0295).
 *
 * Kept out of `assistantEntityCatalog.ts` so the entry chunk that reaches
 * AppLayout does not statically pull every creatable service. Loaders run
 * only inside `data.createRecord`.
 */
import type { AuditWriteContext } from '@syscolect/domain'

/** Minimal surface of {@link OrgScopedCrudService.create} used by the assistant. */
export interface AssistantCreateService {
  create(
    orgId: string,
    data: Record<string, unknown>,
    ctx?: undefined,
    opts?: { audit?: AuditWriteContext },
  ): Promise<{ id: string } & Record<string, unknown>>
}

type ServiceLoader = () => Promise<AssistantCreateService>

/**
 * Entity name → dynamic import of the OrgScopedCrudService singleton.
 * Only entities marked `creatable` in the catalog may appear here.
 */
const LOADERS: Readonly<Record<string, ServiceLoader>> = {
  Brand: async () => {
    const { brandService } = await import('@syscolect/domain')
    return brandService as unknown as AssistantCreateService
  },
  Color: async () => {
    const { colorService } = await import('@syscolect/domain')
    return colorService as unknown as AssistantCreateService
  },
  Unit: async () => {
    const { unitService } = await import('@syscolect/domain')
    return unitService as unknown as AssistantCreateService
  },
  Sector: async () => {
    const { sectorService } = await import('@syscolect/domain')
    return sectorService as unknown as AssistantCreateService
  },
  CostCenter: async () => {
    const { costCenterService } = await import('@syscolect/domain')
    return costCenterService as unknown as AssistantCreateService
  },
  ProductGroup: async () => {
    const { productGroupService } = await import('@syscolect/domain')
    return productGroupService as unknown as AssistantCreateService
  },
  MachineGroup: async () => {
    const { machineGroupService } = await import('@syscolect/domain')
    return machineGroupService as unknown as AssistantCreateService
  },
  ProductionStopReason: async () => {
    const { productionStopReasonService } = await import('@syscolect/domain')
    return productionStopReasonService as unknown as AssistantCreateService
  },
}

/** Resolve the CRUD service for a creatable entity (dynamic import). */
export async function loadCreatableService(
  entity: string,
): Promise<AssistantCreateService | undefined> {
  const loader = LOADERS[entity]
  if (!loader) return undefined
  return loader()
}
